import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { BillingInvoice, Integrator, IntegratorBilling, SubscriptionPlan, UpdateBillingRequest } from '../types';
import { AiBudgetInfo, billingApi, BillingPriceList } from '../api/billing';
import IntegratorAutocomplete from '../components/IntegratorAutocomplete';
import { Toast, useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';

const fmtUsd = (v: number) => `$${Number(v).toFixed(2)}`;

const planBadge: Record<SubscriptionPlan, string> = {
  FREE: 'badge badge-warning',
  STANDARD: 'badge badge-success',
  PRO: 'badge badge-success',
};
const planLabel: Record<SubscriptionPlan, string> = {
  FREE: 'Testowy',
  STANDARD: 'Standard',
  PRO: 'Pro (AI)',
};
const invoiceStatusBadge: Record<string, string> = {
  ISSUED: 'badge badge-success',
  PENDING: 'badge badge-warning',
  FAILED: 'badge badge-danger',
};
const invoiceStatusLabel: Record<string, string> = {
  ISSUED: 'Wystawiona',
  PENDING: 'W toku',
  FAILED: 'Błąd',
};

const formatPln = (v: number | null) =>
  v == null ? '—' : `${v.toFixed(2).replace('.', ',')} zł netto/mies.`;

const InvoiceList: React.FC<{ integratorId: string }> = ({ integratorId }) => {
  const [invoices, setInvoices] = useState<BillingInvoice[] | null>(null);

  useEffect(() => {
    billingApi.invoices(integratorId)
      .then(({ data }) => setInvoices(data))
      .catch(() => setInvoices([]));
  }, [integratorId]);

  if (invoices === null) return <div className="item-meta">Ładowanie faktur…</div>;
  if (invoices.length === 0) return <div className="item-meta">Brak faktur — pierwsza powstanie przy najbliższym przebiegu fakturowania.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem', marginTop: '0.5rem' }}>
      {invoices.map((inv) => (
        <div key={inv.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', flexWrap: 'wrap' }}>
          <span className="mono">{inv.period}</span>
          <span className={invoiceStatusBadge[inv.status]}>{invoiceStatusLabel[inv.status]}</span>
          <span>{planLabel[inv.plan]}</span>
          <span>{formatPln(inv.priceNet)}</span>
          {inv.invoiceNumber && <span className="mono">nr {inv.invoiceNumber}</span>}
          {inv.status === 'FAILED' && inv.errorMessage && (
            <span style={{ color: 'var(--color-danger)' }} title={inv.errorMessage}>
              {inv.errorMessage.length > 80 ? inv.errorMessage.slice(0, 80) + '…' : inv.errorMessage}
            </span>
          )}
        </div>
      ))}
    </div>
  );
};

const IntegratorBillingCard: React.FC<{
  integrator: IntegratorBilling;
  onSaved: (updated: IntegratorBilling) => void;
}> = ({ integrator, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [showInvoices, setShowInvoices] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [plan, setPlan] = useState<SubscriptionPlan>(integrator.plan);
  const [priceOverride, setPriceOverride] = useState(
    integrator.priceNetOverride != null ? String(integrator.priceNetOverride) : ''
  );

  const missingBillingData = integrator.plan !== 'FREE' && !integrator.nip;

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const req: UpdateBillingRequest = {
        plan,
        priceNetOverride: priceOverride.trim() === '' ? null : Number(priceOverride.replace(',', '.')),
      };
      if (req.priceNetOverride != null && (isNaN(req.priceNetOverride) || req.priceNetOverride < 0)) {
        setError('Nieprawidłowa cena indywidualna.');
        setBusy(false);
        return;
      }
      const { data } = await billingApi.update(integrator.id, req);
      onSaved(data);
      setEditing(false);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się zapisać zmian.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', flexWrap: 'wrap' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', textTransform: 'uppercase', letterSpacing: '0.01em' }}>
            {integrator.name}
          </h3>
          <span className={planBadge[integrator.plan]}>{planLabel[integrator.plan]}</span>
          {integrator.plan !== 'FREE' && (
            <span className="eyebrow">{formatPln(integrator.effectivePriceNet)}</span>
          )}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn" onClick={() => setShowInvoices((v) => !v)}>
            {showInvoices ? 'Ukryj faktury' : 'Faktury'}
          </button>
          <button className="btn btn-primary" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Anuluj' : 'Edytuj plan'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        <span className="mono">{integrator.nip ? `NIP ${integrator.nip}` : 'brak NIP'}</span>
        {integrator.billingCity && (
          <span>
            {integrator.billingStreet}
            {integrator.billingBuildingNo ? ` ${integrator.billingBuildingNo}` : ''}
            {integrator.billingApartmentNo ? `/${integrator.billingApartmentNo}` : ''}
            {', '}{integrator.billingPostCode} {integrator.billingCity}
          </span>
        )}
        {integrator.lastInvoice && (
          <span>
            Ostatnia faktura: <span className="mono">{integrator.lastInvoice.period}</span>{' '}
            <span className={invoiceStatusBadge[integrator.lastInvoice.status]}>
              {invoiceStatusLabel[integrator.lastInvoice.status]}
            </span>
          </span>
        )}
      </div>

      {missingBillingData && (
        <div style={{ marginTop: '0.5rem', color: 'var(--color-danger)', fontSize: '0.8125rem' }}>
          Plan płatny bez NIP — faktura nie zostanie wystawiona. Dane firmy uzupełnisz w zakładce „Integratorzy".
        </div>
      )}

      {editing && (
        <form onSubmit={save} style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.75rem', alignItems: 'flex-end' }}>
          <div style={{ flex: '1 1 180px' }}>
            <label className="form-label">Plan</label>
            <select className="form-input" value={plan} onChange={(e) => setPlan(e.target.value as SubscriptionPlan)}>
              <option value="FREE">Free (pilot, bez faktur)</option>
              <option value="STANDARD">Standard</option>
              <option value="PRO">Pro (AI)</option>
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label className="form-label">Cena indywidualna netto (puste = cennik)</label>
            <input className="form-input" value={priceOverride} onChange={(e) => setPriceOverride(e.target.value)} placeholder="np. 199.00" />
          </div>
          <button className="btn btn-primary" type="submit" disabled={busy}>
            {busy ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          <span className="item-meta" style={{ flexBasis: '100%' }}>
            Dane firmy (nazwa, NIP, adres) edytujesz w zakładce „Integratorzy".
          </span>
        </form>
      )}
      {error && <div className="error-message">{error}</div>}

      {showInvoices && <InvoiceList integratorId={integrator.id} />}
    </div>
  );
};

/**
 * Super-admin: miesięczny budżet AI per integrator. Wyszukiwarka po NIP-ie (input
 * z podpowiadaniem), po „Szukaj" pokazuje aktualny próg + wydatek MTD i pozwala go
 * edytować. Puste pole = powrót do domyślnego progu z konfiguracji.
 */
const AiBudgetPanel: React.FC<{ integrators: IntegratorBilling[] }> = ({ integrators }) => {
  const [selected, setSelected] = useState<Integrator | null>(null);
  const [info, setInfo] = useState<AiBudgetInfo | null>(null);
  const [loadedFor, setLoadedFor] = useState<Integrator | null>(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, showToast, dismissToast } = useToast();

  // IntegratorAutocomplete oczekuje typu Integrator — mapujemy z listy billingowej.
  const options = useMemo<Integrator[]>(() => integrators.map((i) => ({
    id: i.id, name: i.name, nip: i.nip,
    billingStreet: i.billingStreet, billingBuildingNo: i.billingBuildingNo,
    billingApartmentNo: i.billingApartmentNo, billingPostCode: i.billingPostCode,
    billingCity: i.billingCity, billingEmail: i.billingEmail,
    logoUrl: null, createdAt: i.createdAt,
  })), [integrators]);

  const search = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const { data } = await billingApi.aiBudget(selected.id);
      setInfo(data);
      setLoadedFor(selected);
      // Pole edytujemy od efektywnego progu; puste = wróć do domyślnego.
      setAmount(data.overrideUsd != null ? String(data.overrideUsd) : String(data.effectiveUsd));
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się pobrać budżetu.');
    } finally {
      setLoading(false);
    }
  };

  const save = async (clear: boolean) => {
    if (!loadedFor) return;
    let value: number | null = null;
    if (!clear) {
      const raw = amount.trim().replace(',', '.');
      value = raw === '' ? null : Number(raw);
      if (value != null && (isNaN(value) || value < 0)) {
        showToast('error', 'Nieprawidłowa kwota budżetu.');
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await billingApi.setAiBudget(loadedFor.id, value);
      setInfo(data);
      setAmount(data.overrideUsd != null ? String(data.overrideUsd) : String(data.effectiveUsd));
      showToast('success', clear || value == null
        ? `Przywrócono domyślny budżet (${fmtUsd(data.defaultUsd)}).`
        : `Zapisano budżet ${fmtUsd(data.effectiveUsd)} dla ${loadedFor.name}.`);
    } catch (e: any) {
      showToast('error', e?.response?.data?.message || 'Nie udało się zapisać budżetu.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ padding: '1.25rem', marginBottom: '1.5rem' }}>
      <h2 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Budżet AI (miesięczny, USD)</h2>
      <p className="item-meta" style={{ marginBottom: '0.75rem' }}>
        Limit sumy kosztów AI integratora w miesiącu (OpenAI + parsowanie LlamaParse).
        Po przekroczeniu funkcje AI są blokowane do końca miesiąca. Puste pole = próg domyślny.
      </p>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <IntegratorAutocomplete
          integrators={options}
          value={selected}
          onChange={(i) => { setSelected(i); setInfo(null); setLoadedFor(null); }}
          label="Integrator (NIP lub nazwa)"
          style={{ flex: '1 1 320px' }}
        />
        <button className="btn btn-primary" type="button" onClick={search} disabled={!selected || loading}>
          {loading ? 'Szukam…' : 'Szukaj'}
        </button>
      </div>

      {info && loadedFor && (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flexBasis: '100%', fontSize: '0.875rem' }}>
            <strong>{loadedFor.name}</strong>{' '}
            <span className="item-meta">
              — wydano w tym miesiącu: <strong>{fmtUsd(info.spentThisMonthUsd)}</strong>
              {' '}/ próg <strong>{fmtUsd(info.effectiveUsd)}</strong>
              {info.overrideUsd == null ? ` (domyślny ${fmtUsd(info.defaultUsd)})` : ' (indywidualny)'}
            </span>
          </div>
          <div style={{ flex: '0 1 200px' }}>
            <label className="form-label">Próg budżetu (USD)</label>
            <input className="form-input" value={amount} inputMode="decimal"
              placeholder={String(info.defaultUsd)}
              onChange={(e) => setAmount(e.target.value)} />
          </div>
          <button className="btn btn-primary" type="button" onClick={() => save(false)} disabled={saving}>
            {saving ? 'Zapisywanie…' : 'Zapisz'}
          </button>
          {info.overrideUsd != null && (
            <button className="btn" type="button" onClick={() => save(true)} disabled={saving}>
              Przywróć domyślny ({fmtUsd(info.defaultUsd)})
            </button>
          )}
        </div>
      )}
      <Toast toast={toast} onDismiss={dismissToast} />
    </div>
  );
};

const Billing: React.FC = () => {
  const { user } = useAuth();
  const [integrators, setIntegrators] = useState<IntegratorBilling[]>([]);
  const [pricing, setPricing] = useState<BillingPriceList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await billingApi.list();
      setIntegrators(data);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Nie udało się pobrać planów.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    billingApi.pricing()
      .then(({ data }) => setPricing(data))
      .catch(() => { /* sekcja cennika po prostu się nie pokaże */ });
  }, []);

  if (!user?.roles?.includes('ADMIN')) {
    return <Navigate to="/machines" replace />;
  }

  const runNow = async () => {
    setRunning(true);
    setRunResult('');
    setError('');
    try {
      const { data } = await billingApi.run();
      setRunResult(`Okres ${data.period}: wystawiono ${data.issued}, błędy ${data.failed}, pominięto ${data.skipped}.`);
      load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się uruchomić fakturowania.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Plany i faktury</h1>
      <p className="item-meta" style={{ marginBottom: '1rem' }}>
        Plany subskrypcji integratorów: Testowy (pilotaż — wszystkie funkcje wraz z AI, bez limitów i bez faktur), Standard (katalog maszyn i zgłoszenia),
        Pro (dodatkowo funkcje AI). Faktury za plany płatne wystawia Fakturownia automatycznie raz w miesiącu
        — każdy integrator ma dokładnie jedną fakturę za okres.
      </p>

      {pricing && (
        <div style={{ maxWidth: 720, margin: '0 auto 1.5rem' }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '1rem',
          }}>
            <div className="card" style={{ padding: '1rem 1.25rem', borderTop: '4px solid #949ba4', textAlign: 'center' }}>
              <div className="eyebrow" style={{ marginBottom: '0.375rem' }}>Testowy</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>0 zł</div>
              <div className="item-meta">pilotaż, bez faktur</div>
            </div>
            <div className="card" style={{ padding: '1rem 1.25rem', borderTop: '4px solid #1e78d6', textAlign: 'center' }}>
              <div className="eyebrow" style={{ marginBottom: '0.375rem' }}>Standard</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                {pricing.standardNet.toFixed(2).replace('.', ',')} zł
              </div>
              <div className="item-meta">netto / mies.</div>
            </div>
            <div className="card" style={{ padding: '1rem 1.25rem', borderTop: '4px solid #2e9956', textAlign: 'center' }}>
              <div className="eyebrow" style={{ marginBottom: '0.375rem' }}>Pro (AI)</div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700 }}>
                {pricing.proNet.toFixed(2).replace('.', ',')} zł
              </div>
              <div className="item-meta">netto / mies.</div>
            </div>
          </div>
          <div className="item-meta" style={{ textAlign: 'center', marginTop: '0.5rem' }}>
            Ceny z konfiguracji (app.billing.price-net.*). Rabat indywidualny ustawisz na karcie integratora poniżej.
          </div>
        </div>
      )}

      <div className="card" style={{ padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn btn-primary" onClick={runNow} disabled={running}>
          {running ? 'Fakturowanie…' : 'Wystaw faktury za bieżący miesiąc'}
        </button>
        <span className="item-meta">
          Bezpieczne do wielokrotnego klikania — wystawia tylko brakujące faktury i ponawia nieudane.
        </span>
        {runResult && <span style={{ fontSize: '0.8125rem' }}>{runResult}</span>}
      </div>

      <AiBudgetPanel integrators={integrators} />

      {error && <div className="card" style={{ color: 'var(--color-danger)', marginBottom: '1rem' }}>{error}</div>}

      {loading ? (
        <div className="empty-state">Ładowanie…</div>
      ) : integrators.length === 0 ? (
        <div className="empty-state">Brak integratorów</div>
      ) : (
        integrators.map((i) => (
          <IntegratorBillingCard
            key={i.id}
            integrator={i}
            onSaved={(updated) => setIntegrators((prev) => prev.map((p) => (p.id === updated.id ? updated : p)))}
          />
        ))
      )}
    </div>
  );
};

export default Billing;
